import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import {
  Box, Typography, Card, Button, TextField, IconButton, CircularProgress, Snackbar, Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { API_BASE_URL } from "../../config";
import { setCurrentProject } from "../../store/slices/projectSlice";
import { createIntakeSession } from "../../services/intakeClient";
import { resolveIntakeStartedAt } from "../../utils/intakeStartedStorage";
import { loadGate0, resolveGate0 } from "../../utils/gate0Storage";

export default function IntakeFormPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const gate0Prefill = resolveGate0(projectId, location.state);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appName, setAppName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (gate0Prefill?.app_name) {
      setAppName(gate0Prefill.app_name);
      setOriginalName(gate0Prefill.app_name);
    }
  }, [gate0Prefill]);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/projects/${projectId}/framework`);
        if (res.data?.success) {
          const name = gate0Prefill?.app_name || res.data.data.project_name || "";
          setAppName(name);
          setOriginalName(name);
        } else {
          setError("Could not load project.");
        }
      } catch (e) {
        setError(e.response?.data?.error || "Failed to load project.");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const handleContinue = async () => {
    const trimmed = appName.trim();
    if (!trimmed) {
      setError("Application name is required.");
      return;
    }
    try {
      setSaving(true);
      setError("");

      // 1. If user changed the name, sync it back to the project (updates the card)
      if (trimmed !== originalName) {
        const res = await axios.put(`${API_BASE_URL}/projects/${projectId}/name`, {
          project_name: trimmed,
        });
        if (!res.data?.success) {
          setError(res.data?.error || "Failed to update application name.");
          setSaving(false);
          return;
        }
        dispatch(setCurrentProject({ id: parseInt(projectId), project_name: trimmed }));
      }

      // 2. Try to create the intake session before entering the chat page.
      //    The Python agent (intake_agent/agent.py) currently only implements
      //    paste/upload/framework — the session/chat endpoints are M2's WIP.
      //    Until those land, this call will 502. We surface a clear message
      //    but still let the user click "Continue to chat" if they want to
      //    see the placeholder.
      try {
        const gate0Data = gate0Prefill || loadGate0(projectId) || { app_name: trimmed };
        if (!gate0Data.app_name) gate0Data.app_name = trimmed;
        const created = await createIntakeSession(projectId, gate0Data);
        resolveIntakeStartedAt(projectId, {
          progressStartedAt: created?.progress?.started_at,
        });
      } catch (e) {
        const raw = e.response?.data?.detail ?? e.response?.data?.error;
        const detail =
          typeof raw === "string" ? raw : raw ? JSON.stringify(raw) : e.message;
        setError(
          `Chat agent doesn't have session/chat endpoints yet. ` +
          `(${e.response?.status || ""} ${detail}). ` +
          `M2 needs to ship them — see Notes for status.`
        );
        setSaving(false);
        return;
      }

      // 3. Now safe to enter the chat page — it will find the session and open.
      navigate(`/chat/${projectId}`);
    } catch (e) {
      setError(e.response?.data?.error || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100%", bgcolor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", px: 3 }}>
      <Card elevation={0} sx={{ maxWidth: 560, width: "100%", border: "1px solid #EBEBEB", borderRadius: 3, p: 5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <IconButton onClick={() => navigate(`/framework/${projectId}`)} size="small" sx={{ border: "1px solid #D9D9D9", borderRadius: 1 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography fontSize="11px" fontWeight={700} color="#999" textTransform="uppercase" letterSpacing={0.5}>
            Intake — Step 1 of 1
          </Typography>
        </Box>

        <Typography variant="h5" fontWeight={700} mb={1}>
          {gate0Prefill ? "Ready for intake chat" : "Confirm your application"}
        </Typography>
        <Typography fontSize="14px" color="#666" mb={3} lineHeight={1.6}>
          {gate0Prefill
            ? "Gate 0 and framework are configured. Start the chatbot — it already has your application basics (name, owner, environment, etc.)."
            : "We'll use this as the application name throughout the intake. You can adjust it now — the card on Home will update automatically."}
        </Typography>

        <TextField
          fullWidth
          label="Application name"
          required
          value={appName}
          onChange={(e) => setAppName(e.target.value)}
          variant="outlined"
          sx={{ mb: 3 }}
          autoFocus
          helperText="Required. Used by the chatbot as the starting point of the conversation."
        />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/framework/${projectId}`)}
            sx={{ textTransform: "none", fontWeight: 600, borderColor: "#D9D9D9", color: "#666" }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disabled={saving || !appName.trim()}
            onClick={handleContinue}
            sx={{ textTransform: "none", fontWeight: 700, px: 3, bgcolor: "#1A6EF5", "&:hover": { bgcolor: "#1251c0" } }}
          >
            {saving ? <CircularProgress size={18} sx={{ color: "white" }} /> : "Continue to chat →"}
          </Button>
        </Box>
      </Card>

      <Snackbar
        open={Boolean(error)}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity="error" onClose={() => setError("")}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
